import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FilePdf, FileText, ChartLine, Info, CheckCircle, Sparkle } from '@phosphor-icons/react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const [reportType, setReportType] = useState('summary');
  const [grantId, setGrantId] = useState('');
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);
  const hasGenerated = useRef(false);
  const [scale, setScale] = useState(1);

  const fetchGrants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tracker`);
      setGrants(response.data);
    } catch (error) {
      console.error('Failed to fetch grants:', error);
    }
  }, []);

  const generateReport = useCallback(async () => {
    setLoading(true);
    // Simulate AI analysis delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    try {
      const mockData = {
        title: reportType === 'summary' ? 'Operations Summary Report' : `Grant Audit: ${grants.find(g => g.id === grantId)?.grant_name || 'Project Alpha'}`,
        id: `EQCA-RPT-${Math.floor(Math.random() * 10000)}`,
        date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        summary: "This report provides an executive analysis of organizational spending and grant compliance. Initial findings indicate that 92% of expenses are correctly mapped to their respective funder requirements. The Youth Development program is currently exceeding its quarterly utilization target by 4.2%, while maintaining strict adherence to administrative overhead caps.",
        compliance_score: 94.5,
        financials: {
          total_allocated: 750000,
          total_spent: 412500,
          remaining: 337500,
          active_grants: 15
        }
      };
      setReport(mockData);
    } finally {
      setLoading(false);
    }
  }, [grantId, grants, reportType]);

  useEffect(() => {
    fetchGrants();
  }, [fetchGrants]);

  useEffect(() => {
    if (!hasGenerated.current) {
      generateReport();
      hasGenerated.current = true;
    }
  }, [generateReport]);

  // Handle mobile scaling for the A4 preview
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 850) {
        setScale((window.innerWidth - 64) / 794);
      } else {
        setScale(1);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const downloadPDF = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        width: 794,
        height: 1123
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`EQCA_OFFICIAL_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('PDF Generation failed:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="h-full overflow-auto p-4 sm:p-6 space-y-6 bg-background text-foreground transition-colors duration-300">
      <div className="border-b border-border pb-4">
        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-2">Reporting & Compliance</h1>
        <p className="text-sm sm:text-base text-muted-foreground">Generate official A4 audit reports and summaries</p>
      </div>

      <div className="bg-card border border-border rounded-md p-4 sm:p-6 flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-bold text-muted-foreground uppercase mb-2">Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
          >
            <option value="summary">Organization Summary</option>
            <option value="grant">Grant Audit Report</option>
            <option value="compliance">Compliance Risk Assessment</option>
          </select>
        </div>

        {reportType === 'grant' && (
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-bold text-muted-foreground uppercase mb-2">Select Grant</label>
            <select
              value={grantId}
              onChange={(e) => setGrantId(e.target.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
            >
              <option value="">Choose a grant...</option>
              {grants.map(g => (
                <option key={g.id} value={g.id}>{g.grant_name}</option>
              ))}
            </select>
          </div>
        )}

        <button
          onClick={generateReport}
          disabled={loading || (reportType === 'grant' && !grantId)}
          className="px-6 py-3 bg-[#E04E39] hover:bg-[#E86B5A] text-white font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </button>
      </div>

      {report && (
        <div className="space-y-4">
          <div className="flex justify-center gap-3">
            <button
              onClick={downloadPDF}
              disabled={downloading}
              className="flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3 bg-[#E04E39] text-white rounded-full hover:bg-[#E86B5A] transition-all font-bold shadow-xl active:scale-95"
            >
              {downloading ? 'Exporting Official A4...' : <><FilePdf size={24} weight="fill" /> DOWNLOAD PDF</>}
            </button>
          </div>

          <div className="flex justify-center bg-muted p-4 sm:p-8 rounded-lg transition-colors duration-300 overflow-hidden">
            <div 
              style={{ 
                transform: `scale(${scale})`, 
                transformOrigin: 'top center',
                width: '794px',
                height: `${1123 * scale}px` // Adjust container height based on scale
              }}
              className="transition-transform duration-200"
            >
              <div 
                ref={reportRef}
                style={{ width: '794px', height: '1123px', padding: '80px' }}
                className="bg-white text-black font-sans leading-normal shadow-2xl flex flex-col relative overflow-hidden text-left"
              >
                {/* A4 Report Content */}
                <div className="flex justify-between items-start mb-16 pb-10 border-b-2 border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-black rounded-lg">
                      <img src="/logo.png" alt="EQCA" className="h-12 w-auto invert" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black tracking-tighter uppercase leading-none">EQCA Ops</h2>
                      <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest font-bold">Official Compliance Division</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Report Reference</p>
                    <p className="text-sm font-mono font-bold">{report.id}</p>
                  </div>
                </div>

                <div className="mb-12">
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-3xl font-black text-gray-900">{report.title}</h3>
                    <p className="text-sm font-bold text-gray-500">{report.date}</p>
                  </div>
                  <div className="h-1.5 w-24 bg-[#E04E39] mb-8"></div>
                  
                  <div className="bg-gray-50 p-6 border-l-4 border-gray-200 mb-10">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Executive Summary</h4>
                    <p className="text-lg font-serif italic text-gray-700 leading-relaxed indent-8">
                      "{report.summary}"
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-12 mb-12">
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 pb-2 border-b">Financial Overview</h4>
                    <div className="space-y-6">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Total Allocation</p>
                        <p className="text-2xl font-bold">${report.financials.total_allocated.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Actual Expenditure</p>
                        <p className="text-2xl font-bold text-[#E04E39]">${report.financials.total_spent.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Remaining Liquidity</p>
                        <p className="text-2xl font-bold text-gray-900">${report.financials.remaining.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 pb-2 border-b">Compliance Metrics</h4>
                    <div className="mb-8">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Reporting Compliance Score</p>
                      <div className="flex items-end gap-3">
                        <p className="text-5xl font-black text-gray-900">{report.compliance_score}%</p>
                        <CheckCircle size={32} weight="fill" className="text-green-500 mb-1" />
                      </div>
                    </div>
                    <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: `${report.compliance_score}%` }}></div>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2 font-medium">Status: HIGHLY COMPLIANT - NO FLAGS DETECTED</p>
                  </div>
                </div>

                <div className="mt-auto pt-10 border-t border-gray-100 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Sparkle size={16} weight="fill" className="text-[#E04E39]" />
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Verified by VANCO SECURE AI</p>
                  </div>
                  <div className="text-[8px] text-gray-300 font-bold uppercase tracking-[0.2em]">
                    EQCA OFFICIAL AUDIT DOCUMENT • PAGE 1 OF 1
                  </div>
                </div>

                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none -rotate-12">
                  <img src="/logo.png" alt="Watermark" className="w-[500px] h-auto grayscale" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
